import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Award, BriefcaseBusiness, ExternalLink, Github, ShieldCheck } from 'lucide-react';
import { Progress } from '../components/ui/progress';
import { getTalentReport } from '../lib/firestore';

const TalentReportPage = () => {
  const { slug } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadReport = useCallback(async () => {
    try {
      const reportData = await getTalentReport(slug);
      setReport(reportData);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
        <div className="text-[#00E5FF] text-xl">Loading candidate report...</div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center px-6">
        <div className="max-w-xl text-center">
          <h1 className="text-3xl font-bold mb-3">Candidate Report Not Found</h1>
          <p className="text-[#94A3B8] mb-6">This report may not be published yet.</p>
          <Link to="/" className="text-[#00E5FF]">Return to Peabsmart Academy</Link>
        </div>
      </div>
    );
  }

  const score = Number(report.hiringReadinessRating || 0);

  return (
    <div className="min-h-screen bg-[#0B0E14] text-[#F8FAFC]">
      <section className="bg-[#151923] border-b border-[#1E293B]">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="flex items-center gap-3 text-[#00E5FF] mb-3">
            <ShieldCheck className="w-5 h-5" />
            Peabsmart Verified Candidate Report
          </div>
          <h1 className="text-4xl font-bold">{report.graduateName}</h1>
          <p className="text-[#94A3B8] mt-2">{report.jobLevel || 'DevOps Candidate'}</p>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <ReportCard icon={<BriefcaseBusiness />} label="Hiring Readiness" value={`${score}%`} />
          <ReportCard icon={<Award />} label="Certification" value={report.certificationStatus} />
          <ReportCard icon={<ShieldCheck />} label="Technical Quiz" value={`${Number(report.technicalQuizScore || 0)}%`} />
          <ReportCard icon={<ShieldCheck />} label="Practical Lab Score" value={`${Number(report.practicalLabScore || 0)}%`} />
        </div>

        {report.verification && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <ReportCard icon={<ShieldCheck />} label="Portfolio Verified" value={report.verification.portfolioVerified ? 'Verified' : 'Pending'} />
            <ReportCard icon={<ShieldCheck />} label="Certification Verified" value={report.verification.certificationVerified ? 'Verified' : 'Pending'} />
            <ReportCard icon={<ShieldCheck />} label="Career Launch" value={report.verification.careerLaunchCompleted ? 'Complete' : 'In Progress'} />
          </div>
        )}

        <div className="bg-[#151923] border border-[#1E293B] rounded-md p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4">Readiness Rating</h2>
          <Progress value={score} className="h-2 mb-4" />
          <p className="text-[#94A3B8]">{report.finalRecommendation}</p>
        </div>

        {(report.selectedChallenge || report.communicationScore) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <ReportCard icon={<BriefcaseBusiness />} label="Practical Challenge" value={report.selectedChallenge} />
            <ReportCard icon={<ShieldCheck />} label="Interview Communication" value={`${Number(report.communicationScore || 0)}%`} />
          </div>
        )}

        {report.scoreBreakdown?.length > 0 && (
          <section className="bg-[#151923] border border-[#1E293B] rounded-md p-6 mb-8">
            <h2 className="text-xl font-bold mb-4">Score Breakdown</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {report.scoreBreakdown.map((item) => (
                <div key={item.label}>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#94A3B8]">{item.label}</span>
                    <span className="font-mono">{Number(item.value || 0)}%</span>
                  </div>
                  <Progress value={Number(item.value || 0)} className="h-2" />
                </div>
              ))}
            </div>
          </section>
        )}

        {report.graduationTasks?.length > 0 && (
          <section className="bg-[#151923] border border-[#1E293B] rounded-md p-6 mb-8">
            <h2 className="text-xl font-bold mb-4">Graduation Tasks</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {report.graduationTasks.map((task) => (
                <div key={task.title} className="bg-[#0B0E14] border border-[#1E293B] rounded-md p-4">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <h3 className="font-bold">{task.title}</h3>
                    <span className={task.score >= task.passingScore ? 'text-[#10B981]' : 'text-[#F59E0B]'}>
                      {Number(task.score || 0)}%
                    </span>
                  </div>
                  <p className="text-sm text-[#94A3B8]">{task.taskTitle}</p>
                  <div className="text-xs text-[#64748B] mt-3">
                    {task.submitted ? 'Submitted for review' : 'Submission pending'} · Pass mark {task.passingScore}%
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
            <h2 className="text-xl font-bold mb-4">Skills Mastered</h2>
            <div className="flex flex-wrap gap-2">
              {(report.skillsMastered || []).map((skill) => (
                <span key={skill} className="px-3 py-1 rounded bg-[#00E5FF]/10 text-[#00E5FF] text-sm">
                  {skill}
                </span>
              ))}
            </div>
          </section>

          <section className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
            <h2 className="text-xl font-bold mb-4">Candidate Links</h2>
            <div className="space-y-3">
              {report.githubProfile && <ReportLink href={report.githubProfile} icon={<Github className="w-4 h-4" />} label="GitHub Profile" />}
              {report.portfolioLink && <ReportLink href={report.portfolioLink} icon={<ExternalLink className="w-4 h-4" />} label="Portfolio" />}
              {report.capstoneRepository && <ReportLink href={report.capstoneRepository} icon={<Github className="w-4 h-4" />} label="Capstone Repository" />}
            </div>
          </section>
        </div>

        <section className="bg-[#151923] border border-[#1E293B] rounded-md p-6 mt-6">
          <h2 className="text-xl font-bold mb-3">Capstone Project Summary</h2>
          <p className="text-[#94A3B8] whitespace-pre-wrap">{report.capstoneProjectSummary}</p>
        </section>

        {(report.systemDesign || report.takeHome) && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <EvidencePanel title="System Design Evidence" data={report.systemDesign} />
            <EvidencePanel title="Take-Home Assignment Evidence" data={report.takeHome} />
          </div>
        )}
      </main>
    </div>
  );
};

const ReportCard = ({ icon, label, value }) => (
  <div className="bg-[#151923] border border-[#1E293B] rounded-md p-5">
    <div className="text-[#00E5FF] mb-3">{icon}</div>
    <div className="text-sm text-[#94A3B8]">{label}</div>
    <div className="text-2xl font-bold mt-1">{value || 'Pending'}</div>
  </div>
);

const ReportLink = ({ href, icon, label }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-[#00E5FF] hover:underline">
    {icon}
    {label}
  </a>
);

const EvidencePanel = ({ title, data }) => {
  const entries = Object.entries(data || {}).filter(([, value]) => value);

  if (!entries.length) return null;

  return (
    <section className="bg-[#151923] border border-[#1E293B] rounded-md p-6">
      <h2 className="text-xl font-bold mb-4">{title}</h2>
      <div className="space-y-3">
        {entries.map(([key, value]) => (
          <div key={key}>
            <div className="text-sm text-[#94A3B8] capitalize">{key.replace(/([A-Z])/g, ' $1')}</div>
            {String(value).startsWith('http') ? (
              <a href={value} target="_blank" rel="noopener noreferrer" className="text-[#00E5FF] break-all hover:underline">{value}</a>
            ) : (
              <p className="text-[#F8FAFC] whitespace-pre-wrap">{value}</p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export default TalentReportPage;
